/** Karşılaştırma için geçerli önceki değer (0/null ise anlamlı baz yok). */
export function effectiveStatPrevious(
  current: number,
  previousMonth: number | null | undefined
): number {
  const prev = previousMonth ?? 0;
  if (prev > 0) return prev;
  return current;
}

/** Önceki ay kaydı varsa fark; yoksa null (yanlış +tam sayı göstermemek için). */
export function statDelta(
  current: number,
  previousMonth: number | null | undefined
): number | null {
  const prev = previousMonth ?? 0;
  if (prev <= 0) return null;
  const d = current - prev;
  return d === 0 ? null : d;
}

export function hasStatBaseline(previousMonth: number | null | undefined): boolean {
  return (previousMonth ?? 0) > 0;
}

/** Trend: artış (up), düşüş (down), sabit (flat), baz yok (none). */
export type StatTrend = 'up' | 'down' | 'flat' | 'none';

export function statTrend(current: number, previousMonth: number | null | undefined): StatTrend {
  if (!hasStatBaseline(previousMonth)) return 'none';
  const prev = previousMonth ?? 0;
  if (current > prev) return 'up';
  if (current < prev) return 'down';
  return 'flat';
}

/** Baz varsa işaretli fark metni ("+12", "-3", sabitse "0"); baz yoksa null (çağıran "—" gösterir). */
export function formatStatDeltaSigned(
  current: number,
  previousMonth: number | null | undefined
): string | null {
  if (!hasStatBaseline(previousMonth)) return null;
  const d = current - (previousMonth ?? 0);
  const sign = d > 0 ? '+' : '';
  return `${sign}${d.toLocaleString('tr-TR')}`;
}

export function formatStatDelta(
  current: number,
  previousMonth: number | null | undefined
): string | null {
  const delta = statDelta(current, previousMonth);
  if (delta === null) return null;
  const sign = delta > 0 ? '+' : '';
  return `(${sign}${delta.toLocaleString('tr-TR')})`;
}

export function formatChangePercent(current: number, previousMonth: number | null | undefined): string {
  const prev = previousMonth ?? 0;
  if (prev <= 0) return '';
  const change = ((current - prev) / prev) * 100;
  return `${change > 0 ? '+' : ''}${change.toFixed(1)}%`;
}

export type ForumCountFields = {
  topics_count: number;
  posts_count: number;
  members_count: number;
  previous_month_topics_count?: number | null;
  previous_month_posts_count?: number | null;
  previous_month_members_count?: number | null;
};

/** Sayılar değiştiyse mevcut değerleri önceki aya yaz; değişmediyse DB’deki önceki ayı koru. */
export function buildPreviousMonthPatch(
  before: ForumCountFields,
  after: Pick<ForumCountFields, 'topics_count' | 'posts_count' | 'members_count'>
): Partial<
  Pick<
    ForumCountFields,
    | 'previous_month_topics_count'
    | 'previous_month_posts_count'
    | 'previous_month_members_count'
  >
> {
  const patch: Partial<
    Pick<
      ForumCountFields,
      | 'previous_month_topics_count'
      | 'previous_month_posts_count'
      | 'previous_month_members_count'
    >
  > = {};

  if (after.topics_count !== before.topics_count) {
    patch.previous_month_topics_count = before.topics_count;
  } else if (hasStatBaseline(before.previous_month_topics_count)) {
    patch.previous_month_topics_count = before.previous_month_topics_count ?? before.topics_count;
  }

  if (after.posts_count !== before.posts_count) {
    patch.previous_month_posts_count = before.posts_count;
  } else if (hasStatBaseline(before.previous_month_posts_count)) {
    patch.previous_month_posts_count = before.previous_month_posts_count ?? before.posts_count;
  }

  if (after.members_count !== before.members_count) {
    patch.previous_month_members_count = before.members_count;
  } else if (hasStatBaseline(before.previous_month_members_count)) {
    patch.previous_month_members_count = before.previous_month_members_count ?? before.members_count;
  }

  return patch;
}

/** Toplu scrape: yalnızca güncellenen alanlar için önceki ay = scrape öncesi mevcut sayı. */
export function buildBulkScrapeCountUpdate(
  forum: ForumCountFields,
  scraped: {
    topics_count: number | null;
    posts_count: number | null;
    members_count: number | null;
  }
): Partial<ForumCountFields> {
  const update: Partial<ForumCountFields> = {};

  if (scraped.topics_count !== null && scraped.topics_count !== undefined) {
    update.previous_month_topics_count = forum.topics_count;
    update.topics_count = scraped.topics_count;
  }
  if (scraped.posts_count !== null && scraped.posts_count !== undefined) {
    update.previous_month_posts_count = forum.posts_count;
    update.posts_count = scraped.posts_count;
  }
  if (scraped.members_count !== null && scraped.members_count !== undefined) {
    update.previous_month_members_count = forum.members_count;
    update.members_count = scraped.members_count;
  }

  return update;
}
